import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { usePasswordResetConfirm } from "@/api/auth";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { createPasswordSchema } from "@/lib/schemas/auth.schema";
import { setDrfErrors } from "@/utils/errors";

const schema = z
	.object({
		password: createPasswordSchema(),
		confirmPassword: z.string(),
	})
	.refine((d) => d.password === d.confirmPassword, {
		path: ["confirmPassword"],
		message: "passwordMismatch",
	});
type FormValues = z.infer<typeof schema>;

export function SetPasswordPage() {
	const { t } = useTranslation();
	const { lang } = useParams<{ lang: string }>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const token = searchParams.get("token") ?? "";
	const confirm = usePasswordResetConfirm();

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<FormValues>({ resolver: zodResolver(schema) });

	function onSubmit(values: FormValues) {
		confirm.mutate(
			{ token, password: values.password },
			{
				onSuccess: () => {
					toast.success(t("setPassword.successMessage"));
					navigate(`/${lang ?? "pl"}/login`);
				},
				onError: (error) => {
					setDrfErrors(error, setError);
					toast.error(t("setPassword.errorMessage"));
				},
			},
		);
	}

	if (!token) {
		return (
			<div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
				<div className="w-full max-w-sm">
					<Card>
						<CardContent className="pt-6">
							<p className="text-sm text-muted-foreground">
								{t("setPassword.invalidToken")}
							</p>
							<Button variant="outline" className="mt-4 w-full" asChild>
								<Link to={`/${lang ?? "pl"}/login`}>
									{t("setPassword.backToLogin")}
								</Link>
							</Button>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
			<div className="w-full max-w-sm">
				<Card>
					<CardHeader>
						<CardTitle>{t("setPassword.title")}</CardTitle>
						<CardDescription>{t("setPassword.description")}</CardDescription>
					</CardHeader>
					<CardContent>
						<form
							onSubmit={handleSubmit(onSubmit)}
							className="flex flex-col gap-4"
						>
							<FieldGroup>
								<Field>
									<FieldLabel htmlFor="password">
										{t("setPassword.passwordLabel")}
									</FieldLabel>
									<Input
										id="password"
										type="password"
										autoComplete="new-password"
										{...register("password")}
									/>
									{errors.password?.message && (
										<FieldError>
											{t(`setPassword.${errors.password.message}`)}
										</FieldError>
									)}
								</Field>
								<Field>
									<FieldLabel htmlFor="confirmPassword">
										{t("setPassword.confirmPasswordLabel")}
									</FieldLabel>
									<Input
										id="confirmPassword"
										type="password"
										autoComplete="new-password"
										{...register("confirmPassword")}
									/>
									{errors.confirmPassword && (
										<FieldError>{t("setPassword.passwordMismatch")}</FieldError>
									)}
								</Field>
							</FieldGroup>
							<Button
								type="submit"
								className="w-full"
								disabled={confirm.isPending}
							>
								{confirm.isPending
									? t("setPassword.saving")
									: t("setPassword.submit")}
							</Button>
						</form>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
